import { ElementType } from '@fiverr-private/bianka_js';
import { CommonElementAttrs, ProfessionBIExtensionData } from '../types';
import { reportProfessionsClick, reportProfessionsImpression } from './reports/professions';
import { createPortfolioSliderEvents, PortfolioBIEvents } from './portfolioSliderEvents';

interface CommonEventAttrs {
    sellerId: string;
    professionExtension: ProfessionBIExtensionData;
}

type BIEventWithCommonElementAttrs = (attrs: CommonElementAttrs) => void;
type BIEventRoleTabClicked = (
    attrs: CommonElementAttrs & { professionExtensionOverride: ProfessionBIExtensionData }
) => void;

interface ProfessionsBIEvents {
    reportSellerHighlightsReviewsClicked: BIEventWithCommonElementAttrs;
    reportSellerHighlightsReviewsImpression: BIEventWithCommonElementAttrs;
    reportSellerHighlightsRatingImpression: BIEventWithCommonElementAttrs;
    reportSellerHighlightsOrdersImpression: BIEventWithCommonElementAttrs;
    reportSellerHighlightsExperienceImpression: BIEventWithCommonElementAttrs;
    reportSellerHighlightsPriceRangeImpression: BIEventWithCommonElementAttrs;
    reportSellerHighlightsRecommendationsClicked: BIEventWithCommonElementAttrs;
    reportSellerHighlightsRecommendationsImpression: BIEventWithCommonElementAttrs;
    reportRoleTabImpression: BIEventWithCommonElementAttrs;
    reportRoleTabClicked: BIEventRoleTabClicked;
}

export interface BIEvents extends ProfessionsBIEvents, PortfolioBIEvents {}

export const createEvents = ({ sellerId, professionExtension }: CommonEventAttrs): BIEvents => ({
    reportSellerHighlightsReviewsClicked: ({ label }: CommonElementAttrs) =>
        reportProfessionsClick({
            sellerId,
            professionExtension,
            eventName: 'clicked_reviews_counter_seller_page',
            componentType: ElementType.LINK,
            componentName: 'reviews_counter',
            componentLabel: label,
        }),
    reportSellerHighlightsReviewsImpression: ({ label }: CommonElementAttrs) =>
        reportProfessionsImpression({
            sellerId,
            professionExtension,
            eventName: 'viewed_reviews_counter_seller_page',
            componentType: 'link',
            componentName: 'reviews_counter',
            componentLabel: label,
        }),
    reportSellerHighlightsRatingImpression: ({ label }: CommonElementAttrs) =>
        reportProfessionsImpression({
            sellerId,
            professionExtension,
            eventName: 'viewed_rating_seller_page',
            componentType: 'text',
            componentName: 'rating',
            componentLabel: label,
        }),
    reportSellerHighlightsOrdersImpression: ({ label }: CommonElementAttrs) =>
        reportProfessionsImpression({
            sellerId,
            professionExtension,
            eventName: 'viewed_orders_counter_seller_page',
            componentType: 'text',
            componentName: 'orders_counter',
            componentLabel: label,
        }),
    reportSellerHighlightsExperienceImpression: ({ label }: CommonElementAttrs) =>
        reportProfessionsImpression({
            sellerId,
            professionExtension,
            eventName: 'viewed_experience_seller_page',
            componentType: 'text',
            componentName: 'experience',
            componentLabel: label,
        }),
    reportSellerHighlightsPriceRangeImpression: ({ label }: CommonElementAttrs) =>
        reportProfessionsImpression({
            sellerId,
            professionExtension,
            eventName: 'viewed_price_range_seller_page',
            componentType: 'text',
            componentName: 'price_range',
            componentLabel: label,
        }),
    reportSellerHighlightsRecommendationsClicked: ({ label }: CommonElementAttrs) =>
        reportProfessionsClick({
            sellerId,
            professionExtension,
            eventName: 'clicked_recommendations_seller_page',
            componentType: ElementType.LINK,
            componentName: 'recommendations',
            componentLabel: label,
        }),
    reportSellerHighlightsRecommendationsImpression: ({ label }: CommonElementAttrs) =>
        reportProfessionsImpression({
            sellerId,
            professionExtension,
            eventName: 'viewed_recommendations_seller_page',
            componentType: 'link',
            componentName: 'recommendations',
            componentLabel: label,
        }),
    reportRoleTabImpression: ({ label }: CommonElementAttrs) =>
        reportProfessionsImpression({
            sellerId,
            professionExtension,
            eventName: 'viewed_tab_seller_page',
            componentType: 'tab',
            componentName: 'tab',
            componentLabel: label,
        }),
    reportRoleTabClicked: ({
        label,
        professionExtensionOverride,
    }: CommonElementAttrs & { professionExtensionOverride: ProfessionBIExtensionData }) =>
        reportProfessionsClick({
            sellerId,
            professionExtension: professionExtensionOverride || professionExtension,
            eventName: 'clicked_tab_seller_page',
            componentType: ElementType.TAB,
            componentName: 'tab',
            componentLabel: label,
        }),

    ...createPortfolioSliderEvents({
        sellerId,
        sellerProfessionId: professionExtension.profession?.sellerProfessionId,
    }),
});
